import { isBrowserFetchTransportError } from '@lib/shared/utils/is-browser-fetch-transport-error';
import * as Sentry from '@sentry/nextjs';

import { sentryConfig } from '../utils/sentry-config';

/**
 * Capture a client exception to Sentry.
 *
 * Browser fetch transport failures (`Failed to fetch` / `network error` /
 * `Load failed` / Firefox NetworkError, including `apiFetch`-wrapped status-0
 * ApiErrors) are skipped — they have no HTTP status and are connectivity
 * noise. Callers that already show user-facing recovery should still call
 * this; suppression is centralized here.
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): string | undefined {
  if (!sentryConfig.enabled) return undefined;
  if (isBrowserFetchTransportError(error)) return undefined;

  let eventId: string | undefined;
  Sentry.withScope(scope => {
    if (context) {
      scope.setContext('extra', context);
      // Promote a `source` field to a first-class tag so it's filterable and
      // groupable in Sentry search + dashboards (not just visible on the issue).
      if (typeof context.source === 'string') {
        scope.setTag('source', context.source);
      }
    }
    eventId = Sentry.captureException(error);
  });
  return eventId;
}
